import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { submissionApi, campaignApi, auth } from '../../services/api';
import { AdminHeader, PrimaryBtn, GhostBtn, EmptyState } from './AdminLayout';
import { useLightbox } from '../../components/Lightbox';

const QBB_LABEL = { disabled: '待接入', pending: '待发布', sent: '已发券宝宝', failed: '发布失败' };
const STATUS_LABEL = { new: '新投稿', promoted: '已进文章池', archived: '已归档' };
const CHANNEL_LABEL = { post: '帖子', review: '店铺评价' };

export default function Submissions() {
  const { state } = useLocation();
  const isSuper = auth.isSuperadmin();
  const merchantId = state?.merchantId;
  const merchantName = state?.merchantName;
  const scoped = !!merchantId;

  const [tab, setTab] = useState('all'); // all | universal | store
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState([]);
  const [promoting, setPromoting] = useState(null);
  const [targetId, setTargetId] = useState('');
  const [busyId, setBusyId] = useState('');
  const [categories, setCategories] = useState([]);
  const [publishing, setPublishing] = useState(null); // 待发帖子的 submission
  const [pubCategory, setPubCategory] = useState('ai');
  const lb = useLightbox();

  const load = () => {
    setLoading(true);
    const params = scoped
      ? { merchantId }
      : (tab === 'all' ? {} : { scope: tab });
    submissionApi.list(params).then(setList).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [merchantId, tab]);

  useEffect(() => {
    campaignApi.list().then((all) =>
      setTargets((all || []).filter((x) => (x.codeType || 'article') === 'article'))
    ).catch(() => {});
    submissionApi.categories().then((cs) => setCategories(cs || [])).catch(() => {});
  }, []);

  const openPromote = (sub) => { setPromoting(sub); setTargetId(targets[0]?._id || ''); };

  const doPromote = async () => {
    if (!promoting || !targetId) return;
    setBusyId(promoting._id);
    try { await submissionApi.promote(promoting._id, targetId); setPromoting(null); load(); }
    catch (e) { alert(e?.response?.data?.error || '进入文章池失败'); }
    finally { setBusyId(''); }
  };

  // 评价直接发；帖子先让运营选分类（含 AI 智能选）
  const onPublishClick = (s) => {
    if (s.targetChannel === 'post') { setPublishing(s); setPubCategory('ai'); }
    else runPublish(s._id);
  };

  const runPublish = async (id, categoryId) => {
    setBusyId(id);
    try {
      const r = await submissionApi.publish(id, categoryId);
      if (r.qbbStatus === 'failed') alert('发布失败：' + (r.qbbError || '未知错误'));
      setPublishing(null);
      load();
    } catch (e) { alert(e?.response?.data?.error || '发布失败'); }
    finally { setBusyId(''); }
  };

  const doArchive = async (id) => { await submissionApi.archive(id); load(); };
  const doDelete = async (id) => { if (window.confirm('确认删除这条投稿？')) { await submissionApi.delete(id); load(); } };

  const title = scoped ? `${merchantName || '店铺'} · 博主投稿` : '博主文章列表';

  return (
    <div style={styles.page}>
      <AdminHeader title={title} back={true} />

      {!scoped && (
        <div style={styles.tabs}>
          {[['all', '全部'], ['universal', '通用码'], ['store', '分店铺']].map(([k, label]) => (
            <span key={k} style={{ ...styles.tab, ...(tab === k ? styles.tabOn : {}) }} onClick={() => setTab(k)}>{label}</span>
          ))}
        </div>
      )}

      <div style={styles.body}>
        {loading ? (
          <div style={styles.skeletons}>{[1, 2, 3].map(i => <div key={i} style={styles.skeleton} />)}</div>
        ) : list.length === 0 ? (
          <EmptyState text="暂无博主投稿" />
        ) : (
          <div style={styles.list}>
            {list.map((s) => (
              <div key={s._id} style={styles.card}>
                <div style={styles.cardTop}>
                  <span style={styles.author}>{s.authorNickname || s.authorPhone || '匿名'}</span>
                  {s.source === 'tandian' && <span style={styles.tandianTag}>探店</span>}
                  <span style={styles.scopeTag}>{s.scope === 'universal' ? '通用' : (s.merchantName || '店铺')}</span>
                  <span style={styles.chan}>{CHANNEL_LABEL[s.targetChannel] || s.targetChannel}</span>
                </div>
                <div style={styles.tagRow}>
                  <span style={{ ...styles.tag, ...tagStyle(s.status) }}>{STATUS_LABEL[s.status] || s.status}</span>
                  <span style={styles.qbbTag}>{QBB_LABEL[s.qbbStatus] || s.qbbStatus}</span>
                  <span style={styles.time}>{new Date(s.createdAt).toLocaleString('zh-CN')}</span>
                </div>
                {s.title && <div style={styles.title}>{s.title}</div>}
                <div style={styles.content}>{s.content}</div>
                {s.images?.length > 0 && (
                  <div style={styles.imgRow}>
                    {s.images.slice(0, 6).map((u, i) => (
                      <img key={i} src={u} alt="" style={styles.thumb} onClick={() => lb.open(s.images, i)} />
                    ))}
                  </div>
                )}
                <div style={styles.actions}>
                  {s.source === 'tandian' && s.externalUrl && (
                    <a style={styles.xhsLink} href={s.externalUrl} target="_blank" rel="noreferrer">看小红书</a>
                  )}
                  {s.qbbStatus !== 'sent' && s.status !== 'archived' && (
                    <PrimaryBtn small onClick={() => onPublishClick(s)}>
                      {busyId === s._id ? '发送中...' : (s.qbbStatus === 'failed' ? '重发券宝宝' : '发到券宝宝')}
                    </PrimaryBtn>
                  )}
                  {s.source !== 'tandian' && s.status === 'new' && <GhostBtn small onClick={() => openPromote(s)}>进入文章池</GhostBtn>}
                  {s.status !== 'archived' && <GhostBtn small onClick={() => doArchive(s._id)}>归档</GhostBtn>}
                  {isSuper && <GhostBtn small onClick={() => doDelete(s._id)}>删除</GhostBtn>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lb.node}

      {publishing && (
        <div style={styles.overlay} onClick={() => setPublishing(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>发到券宝宝帖子</p>
            <p style={styles.modalLabel}>选择分类</p>
            <select style={styles.select} value={pubCategory} onChange={(e) => setPubCategory(e.target.value)}>
              <option value="ai">AI 智能选择</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={styles.modalBtns}>
              <GhostBtn onClick={() => setPublishing(null)}>取消</GhostBtn>
              <PrimaryBtn
                onClick={() => runPublish(publishing._id, pubCategory === 'ai' ? undefined : Number(pubCategory))}
                disabled={busyId === publishing._id}
              >
                {busyId === publishing._id ? '发送中...' : '确认发布'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      {promoting && (
        <div style={styles.overlay} onClick={() => setPromoting(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>进入文章池</p>
            <p style={styles.modalHint}>将博主内容改写文案 + 扰动图片后，追加到所选活动码活动的文章池。</p>
            <p style={styles.modalLabel}>目标活动（活动码）</p>
            {targets.length === 0 ? (
              <p style={styles.warn}>暂无活动码活动，请先创建一个。</p>
            ) : (
              <select style={styles.select} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                {targets.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
            )}
            <div style={styles.modalBtns}>
              <GhostBtn onClick={() => setPromoting(null)}>取消</GhostBtn>
              <PrimaryBtn onClick={doPromote} disabled={!targetId || busyId === promoting._id}>
                {busyId === promoting._id ? '加工中...' : '确认进池'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function tagStyle(status) {
  if (status === 'promoted') return { color: '#16a34a', background: '#f0fdf4', borderColor: '#bbf7d0' };
  if (status === 'archived') return { color: '#9ca3af', background: '#f3f4f6', borderColor: '#e5e7eb' };
  return { color: '#FA1C51', background: '#fff7f8', borderColor: '#ffd6de' };
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  tabs: { display: 'flex', gap: 8, padding: '10px 12px', background: '#fff', borderBottom: '1px solid #f0f0f0' },
  tab: { padding: '6px 14px', fontSize: 14, color: '#666', borderRadius: 16, background: '#f2f2f3', cursor: 'pointer' },
  tabOn: { color: '#fff', background: '#FA1C51', fontWeight: 600 },
  body: { flex: 1, padding: '12px 12px 24px' },
  skeletons: { display: 'flex', flexDirection: 'column', gap: 10 },
  skeleton: { height: 150, borderRadius: 12, background: '#efefef' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  author: { fontSize: 14, fontWeight: 700, color: '#111', flex: 1 },
  scopeTag: { fontSize: 11, color: '#6b7280', background: '#f3f4f6', borderRadius: 4, padding: '1px 6px' },
  chan: { fontSize: 11, color: '#2563eb', background: '#eff6ff', borderRadius: 4, padding: '1px 6px' },
  tandianTag: { fontSize: 11, color: '#7c3aed', background: '#f5f3ff', borderRadius: 4, padding: '1px 6px', fontWeight: 600 },
  xhsLink: { fontSize: 13, color: '#FA1C51', fontWeight: 600, textDecoration: 'none', alignSelf: 'center' },
  tagRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 },
  tag: { fontSize: 11, borderRadius: 4, padding: '1px 6px', border: '1px solid' },
  qbbTag: { fontSize: 11, color: '#6b7280', background: '#f3f4f6', borderRadius: 4, padding: '1px 6px' },
  time: { fontSize: 11, color: '#9ca3af', marginLeft: 'auto' },
  title: { fontSize: 15, fontWeight: 600, color: '#111', marginBottom: 6 },
  content: { fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 10 },
  imgRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 },
  thumb: { width: 64, height: 64, objectFit: 'cover', borderRadius: 6 },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: 360 },
  modalTitle: { fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 8px' },
  modalHint: { fontSize: 12, color: '#6b7280', margin: '0 0 14px', lineHeight: 1.6 },
  modalLabel: { fontSize: 12, color: '#6b7280', margin: '0 0 6px', fontWeight: 500 },
  select: { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' },
  warn: { fontSize: 13, color: '#b45309', background: '#fffbeb', borderRadius: 8, padding: 10 },
  modalBtns: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
};
